import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Target, DollarSign, Truck, Users, Clock, Loader2, Save, RotateCcw, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ObjectivesConfig = () => {
  const { toast } = useToast();
  const [objectives, setObjectives] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchObjectives = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/objectives`, { headers });
      setObjectives(res.data);
      setFormData(JSON.parse(JSON.stringify(res.data)));
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les objectifs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchObjectives(); }, [fetchObjectives]);

  const updateField = (section, key, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [section]: { ...prev[section], [key]: Number(value) || 0 }
      };
      setHasChanges(JSON.stringify(updated) !== JSON.stringify(objectives));
      return updated;
    });
  };

  const saveObjectives = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/objectives`, formData, { headers });
      toast({ title: 'Objectifs sauvegardés', description: 'Les objectifs ont été mis à jour avec succès' });
      setObjectives(JSON.parse(JSON.stringify(formData)));
      setHasChanges(false);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData(JSON.parse(JSON.stringify(objectives)));
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const InputField = ({ label, value, onChange, unit }) => (
    <div>
      <label className="text-xs font-medium text-gray-500 block mb-1">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
          placeholder="0"
          data-testid={`obj-input-${label.toLowerCase().replace(/\s/g, '-')}`}
        />
        {unit && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{unit}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="objectives-config">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Target className="h-5 w-5 text-emerald-600" />
            Configuration des Objectifs
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Définissez vos objectifs business. Ils seront affichés dans le Dashboard.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">Modifications non sauvegardées</Badge>
          )}
          <Button variant="outline" size="sm" onClick={resetForm} disabled={!hasChanges || saving}>
            <RotateCcw className="h-4 w-4 mr-1" />
            Annuler
          </Button>
          <Button
            size="sm"
            onClick={saveObjectives}
            disabled={!hasChanges || saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-objectives-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Revenue Objectives */}
      <Card className="border-l-4 border-l-emerald-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-600" />
            Objectifs de Chiffre d'Affaires
          </CardTitle>
          <CardDescription>Revenus cibles par période (en FCFA)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InputField label="Semaine" value={formData?.revenue?.weekly} onChange={v => updateField('revenue', 'weekly', v)} unit="FCFA" />
            <InputField label="Mois" value={formData?.revenue?.monthly} onChange={v => updateField('revenue', 'monthly', v)} unit="FCFA" />
            <InputField label="Trimestre" value={formData?.revenue?.quarterly} onChange={v => updateField('revenue', 'quarterly', v)} unit="FCFA" />
            <InputField label="Année" value={formData?.revenue?.yearly} onChange={v => updateField('revenue', 'yearly', v)} unit="FCFA" />
          </div>
        </CardContent>
      </Card>

      {/* Operations Objectives */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            Objectifs Opérationnels
          </CardTitle>
          <CardDescription>Nombre d'opérations cibles par période</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InputField label="Jour" value={formData?.operations?.daily} onChange={v => updateField('operations', 'daily', v)} unit="ops" />
            <InputField label="Semaine" value={formData?.operations?.weekly} onChange={v => updateField('operations', 'weekly', v)} unit="ops" />
            <InputField label="Mois" value={formData?.operations?.monthly} onChange={v => updateField('operations', 'monthly', v)} unit="ops" />
            <InputField label="Année" value={formData?.operations?.yearly} onChange={v => updateField('operations', 'yearly', v)} unit="ops" />
          </div>
        </CardContent>
      </Card>

      {/* Collection & Clients */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Collection */}
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Objectifs Recouvrement
            </CardTitle>
            <CardDescription>Taux et délais cibles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Taux de recouvrement" value={formData?.collection?.target_rate_percent} onChange={v => updateField('collection', 'target_rate_percent', v)} unit="%" />
              <InputField label="DSO cible" value={formData?.collection?.target_dso_days} onChange={v => updateField('collection', 'target_dso_days', v)} unit="jours" />
            </div>
          </CardContent>
        </Card>

        {/* New Clients */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              Objectifs Acquisition Clients
            </CardTitle>
            <CardDescription>Nombre de nouveaux clients cibles</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Par mois" value={formData?.new_clients?.monthly} onChange={v => updateField('new_clients', 'monthly', v)} unit="clients" />
              <InputField label="Par trimestre" value={formData?.new_clients?.quarterly} onChange={v => updateField('new_clients', 'quarterly', v)} unit="clients" />
            </div>
          </CardContent>
        </Card>
      </div>

      {objectives?.updated_at && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Dernière modification : {new Date(objectives.updated_at).toLocaleString('fr-FR')}
        </p>
      )}
    </div>
  );
};

export default ObjectivesConfig;
